import React, { useState, useRef, useEffect, useCallback } from 'react';
import JakeMessage from '../../components/JakeMessage';
import './HubAssistant.css';

const HubAssistant = () => {
  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [attachedImages, setAttachedImages] = useState([]); // [{ file, preview, base64, mediaType }]

  const MAX_ATTACHED_IMAGES = 10;
  const MAX_ATTACHED_IMAGE_BYTES = 10 * 1024 * 1024; // 10MB per image
  const MAX_TOTAL_ATTACHED_BYTES = 40 * 1024 * 1024; // 40MB combined raw size

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input on mount and after streaming
  useEffect(() => {
    if (!isStreaming) {
      inputRef.current?.focus();
    }
  }, [isStreaming]);

  // Load conversations on mount
  useEffect(() => {
    loadConversations();
  }, []);

  const loadConversations = async () => {
    try {
      const response = await fetch('/api/jake/conversations', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setConversations(data.conversations || []);
      }
    } catch (error) {
      console.error('Failed to load conversations:', error);
    }
  };

  const loadConversation = async (conversationId) => {
    try {
      const response = await fetch(`/api/jake/conversations/${conversationId}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages.map(m => ({
          id: m.id,
          role: m.role,
          content: m.content,
          timestamp: new Date(m.created_at)
        })));
        setActiveConversationId(conversationId);
      }
    } catch (error) {
      console.error('Failed to load conversation:', error);
    }
  };

  const startNewChat = () => {
    setActiveConversationId(null);
    setMessages([]);
    setInput('');
    setAttachedImages([]);
    inputRef.current?.focus();
  };

  // Handle image file selection — supports selecting/adding multiple at once
  const handleImagesSelect = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ''; // reset so the same file(s) can be selected again
    if (files.length === 0) return;

    let combinedBytes = attachedImages.reduce((sum, img) => sum + img.file.size, 0);
    const accepted = [];

    for (const file of files) {
      if (attachedImages.length + accepted.length >= MAX_ATTACHED_IMAGES) {
        alert(`You can attach up to ${MAX_ATTACHED_IMAGES} images at once.`);
        break;
      }
      if (!file.type.startsWith('image/')) {
        alert(`${file.name} isn't an image file — skipped.`);
        continue;
      }
      if (file.size > MAX_ATTACHED_IMAGE_BYTES) {
        alert(`${file.name} is over 10MB — skipped.`);
        continue;
      }
      if (combinedBytes + file.size > MAX_TOTAL_ATTACHED_BYTES) {
        alert(`Adding ${file.name} would exceed the 40MB combined limit for attached images — skipped.`);
        continue;
      }
      combinedBytes += file.size;
      accepted.push(file);
    }

    if (accepted.length === 0) return;

    Promise.all(accepted.map((file) => new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        resolve({
          file,
          preview: event.target.result,
          base64: event.target.result.split(',')[1], // Remove data:image/xxx;base64, prefix
          mediaType: file.type
        });
      };
      reader.readAsDataURL(file);
    }))).then((newImages) => {
      setAttachedImages((prev) => [...prev, ...newImages]);
    });
  };

  const removeAttachedImage = (index) => {
    setAttachedImages((prev) => prev.filter((_, i) => i !== index));
  };

  const deleteConversation = async (e, conversationId) => {
    e.stopPropagation();
    try {
      await fetch(`/api/jake/conversations/${conversationId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      setConversations(prev => prev.filter(c => c.conversation_id !== conversationId));
      if (activeConversationId === conversationId) {
        startNewChat();
      }
    } catch (error) {
      console.error('Failed to delete conversation:', error);
    }
  };

  const sendMessage = useCallback(async () => {
    if (!input.trim() || isStreaming) return;

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: input.trim(),
      timestamp: new Date(),
      images: attachedImages.map((img) => img.preview) // Include image previews for display
    };

    setMessages(prev => [...prev, userMessage]);
    const imagesToSend = attachedImages; // Capture before clearing
    setInput('');
    setAttachedImages([]);
    setIsStreaming(true);

    // Add placeholder for assistant response
    const assistantId = Date.now() + 1;
    setMessages(prev => [...prev, {
      id: assistantId,
      role: 'assistant',
      content: '',
      status: 'On it!',
      timestamp: new Date()
    }]);

    try {
      console.log('Sending message to Jake:', userMessage.content, imagesToSend.length ? `(with ${imagesToSend.length} image(s))` : '');
      const response = await fetch('/api/jake/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          question: userMessage.content,
          conversationId: activeConversationId,
          model: 'sonnet',
          images: imagesToSend.map((img) => ({ base64: img.base64, mediaType: img.mediaType }))
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Jake API error:', response.status, errorText);
        throw new Error(`Request failed: ${response.status} - ${errorText}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              console.log('SSE data:', data);
              if (data.type === 'text') {
                fullContent += data.content;
                setMessages(prev => prev.map(m =>
                  m.id === assistantId ? { ...m, content: fullContent, status: null } : m
                ));
              } else if (data.type === 'thinking') {
                setMessages(prev => prev.map(m =>
                  m.id === assistantId ? { ...m, status: data.content } : m
                ));
              } else if (data.type === 'done') {
                if (data.conversationId && !activeConversationId) {
                  setActiveConversationId(data.conversationId);
                  loadConversations();
                }
              }
            } catch (e) {
              // Skip parse errors
            }
          }
        }
      }
    } catch (error) {
      console.error('Error:', error);
      setMessages(prev => prev.map(m =>
        m.id === assistantId
          ? { ...m, content: 'Sorry, something went wrong. Please try again.' }
          : m
      ));
    } finally {
      setIsStreaming(false);
    }
  }, [input, isStreaming, activeConversationId, attachedImages]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatDate = (date) => {
    const now = new Date();
    const d = new Date(date);
    const diffDays = Math.floor((now - d) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return d.toLocaleDateString('en-US', { weekday: 'long' });
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="jake-container">
      {/* Sidebar */}
      <div className={`jake-sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="jake-sidebar-header">
          <button className="jake-new-chat-btn" onClick={startNewChat}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M5 12h14" />
            </svg>
            New Chat
          </button>
        </div>

        <div className="jake-conversations-list">
          {conversations.length === 0 ? (
            <div className="jake-no-conversations">No conversations yet</div>
          ) : (
            conversations.map(conv => (
              <div
                key={conv.conversation_id}
                className={`jake-conversation-item ${activeConversationId === conv.conversation_id ? 'active' : ''}`}
                onClick={() => loadConversation(conv.conversation_id)}
              >
                <div className="jake-conversation-title">
                  {conv.title || 'New conversation'}
                </div>
                <div className="jake-conversation-date">
                  {formatDate(conv.created_at)}
                </div>
                <button
                  className="jake-delete-btn"
                  onClick={(e) => deleteConversation(e, conv.conversation_id)}
                  title="Delete conversation"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                  </svg>
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Toggle sidebar button */}
      <button
        className="jake-sidebar-toggle"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        title={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          {sidebarOpen ? (
            <path d="M11 19l-7-7 7-7M18 19l-7-7 7-7" />
          ) : (
            <path d="M13 5l7 7-7 7M6 5l7 7-7 7" />
          )}
        </svg>
      </button>

      {/* Main chat area */}
      <div className="jake-main">
        <div className="jake-messages">
          {messages.length === 0 ? (
            <div className="jake-welcome">
              <div className="jake-welcome-icon">J</div>
              <h1>Hey, I'm Jake!</h1>
              <p>Your AI assistant for Fitness First Sports. Ask me about sales, inventory, customers, orders, and more.</p>
              <div className="jake-suggestions">
                <button onClick={() => { setInput('How are sales this month?'); inputRef.current?.focus(); }}>
                  How are sales this month?
                </button>
                <button onClick={() => { setInput('What are our top selling products?'); inputRef.current?.focus(); }}>
                  Top selling products
                </button>
                <button onClick={() => { setInput('Show me low stock items'); inputRef.current?.focus(); }}>
                  Low stock items
                </button>
              </div>
            </div>
          ) : (
            <>
              {messages.map(message => (
                <div key={message.id} className={`jake-message jake-message-${message.role}`}>
                  {message.role === 'assistant' && (
                    <div className="jake-message-avatar">J</div>
                  )}
                  <div className="jake-message-content">
                    {message.role === 'assistant' ? (
                      <JakeMessage content={message.content} />
                    ) : (
                      <div className="jake-message-text">
                        {message.images && message.images.length > 0 && (
                          <div className="jake-message-image-list">
                            {message.images.map((src, i) => (
                              <img
                                key={i}
                                src={src}
                                alt={`Attached ${i + 1}`}
                                className="jake-message-image"
                              />
                            ))}
                          </div>
                        )}
                        {message.content}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {isStreaming && messages[messages.length - 1]?.content === '' && (
                <div className="jake-message jake-message-assistant">
                  <div className="jake-message-avatar">J</div>
                  <div className="jake-message-content">
                    <div className="jake-status">
                      <div className="jake-typing">
                        <span></span><span></span><span></span>
                      </div>
                      {messages[messages.length - 1]?.status && (
                        <span className="jake-status-text">{messages[messages.length - 1].status}</span>
                      )}
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Input area */}
        <div className="jake-input-container">
          {/* Image previews */}
          {attachedImages.length > 0 && (
            <div className="jake-image-preview-list">
              {attachedImages.map((img, i) => (
                <div className="jake-image-preview" key={img.file.name + img.file.lastModified + i}>
                  {img.preview ? (
                    <img src={img.preview} alt={`Attached ${i + 1}`} />
                  ) : (
                    <div className="jake-image-preview-loading" />
                  )}
                  <button
                    className="jake-image-preview-remove"
                    onClick={() => removeAttachedImage(i)}
                    title="Remove image"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="jake-input-box">
            {/* Hidden file input */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImagesSelect}
              accept="image/*"
              multiple
              style={{ display: 'none' }}
            />
            {/* Attach button */}
            <button
              className="jake-attach-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={isStreaming}
              title="Attach image"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
              </svg>
            </button>
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={attachedImages.length > 0 ? "What should I do with these images?" : "Message Jake..."}
              rows={1}
              disabled={isStreaming}
            />
            <button
              className="jake-send-btn"
              onClick={sendMessage}
              disabled={!input.trim() || isStreaming}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
              </svg>
            </button>
          </div>
          <div className="jake-input-hint">Press Enter to send, Shift+Enter for new line</div>
        </div>
      </div>
    </div>
  );
};

export default HubAssistant;
